import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import EmailTemplateApiService, { EmailTemplate, CreateEmailTemplateRequest, UpdateEmailTemplateRequest } from 'app/apiService/emailTemplate/emailTemplateApiService';

// ----------- Type Definitions -----------
interface EmailTemplateContextType {
  loading: boolean;
  error: string | null;
  templates: EmailTemplate[];
  selectedTemplate: EmailTemplate | null;
  meta: any;
  
  // API Methods
  getEmailTemplates: (dealerId: string, params?: any) => Promise<void>;
  getEmailTemplateById: (dealerId: string, templateId: string) => Promise<void>;
  createEmailTemplate: (dealerId: string, data: CreateEmailTemplateRequest) => Promise<EmailTemplate | undefined>;
  updateEmailTemplate: (dealerId: string, templateId: string, data: UpdateEmailTemplateRequest) => Promise<void>;
  deleteEmailTemplate: (dealerId: string, templateId: string) => Promise<void>;
  
  // Helper Methods
  setSelectedTemplate: (template: EmailTemplate | null) => void;
  clearError: () => void;
  clearTemplates: () => void;
}

// ----------- Context Create -----------
const EmailTemplateContext = createContext<EmailTemplateContextType | undefined>(undefined);

// ----------- Provider Component -----------
export const EmailTemplateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [meta, setMeta] = useState<any>(null);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Clear templates
  const clearTemplates = useCallback(() => {
    setTemplates([]);
    setSelectedTemplate(null);
    setMeta(null);
  }, []);

  // Get email templates
  const getEmailTemplates = useCallback(async (dealerId: string, params?: any) => {
    if (!dealerId) {
      setError('Dealer ID is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await EmailTemplateApiService.getEmailTemplates(dealerId, params);
      
      if (response.body && response.body.data) {
        setTemplates(response.body.data);
        setMeta({
          total: response.body.total,
          page: response.body.page,
          limit: response.body.limit,
        });
      } else {
        setTemplates([]);
        setMeta(null);
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to fetch email templates';
      setError(errorMessage);
      setTemplates([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get email template by ID
  const getEmailTemplateById = useCallback(async (dealerId: string, templateId: string) => {
    if (!dealerId || !templateId) {
      setError('Dealer ID and Template ID are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await EmailTemplateApiService.getEmailTemplateById(dealerId, templateId);
      
      if (response.body) {
        setSelectedTemplate(response.body);
      } else {
        setSelectedTemplate(null);
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to fetch email template';
      setError(errorMessage);
      setSelectedTemplate(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Create email template
  const createEmailTemplate = useCallback(async (dealerId: string, data: CreateEmailTemplateRequest) => {
    if (!dealerId) {
      setError('Dealer ID is required');
      return;
    }

    if (!data.name || !data.subject || !data.html) {
      setError('Template name, subject, and HTML content are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await EmailTemplateApiService.createEmailTemplate(dealerId, data);
      
      if (response.body) {
        // Add the new template to the list
        setTemplates(prev => [response.body, ...prev]);
        setSelectedTemplate(response.body);
      }
      
      return response.body;
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to create email template';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update email template
  const updateEmailTemplate = useCallback(async (dealerId: string, templateId: string, data: UpdateEmailTemplateRequest) => {
    if (!dealerId || !templateId) {
      setError('Dealer ID and Template ID are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await EmailTemplateApiService.updateEmailTemplate(dealerId, templateId, data);
      
      if (response.body) {
        // Update the template in the list
        setTemplates(prev => prev.map(template => 
          template.id === templateId ? response.body : template
        ));
        
        // Update selected template if it's the one being updated
        if (selectedTemplate?.id === templateId) {
          setSelectedTemplate(response.body);
        }
      }
      
      return response.body;
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to update email template';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedTemplate]);

  // Delete email template
  const deleteEmailTemplate = useCallback(async (dealerId: string, templateId: string) => {
    if (!dealerId || !templateId) {
      setError('Dealer ID and Template ID are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await EmailTemplateApiService.deleteEmailTemplate(dealerId, templateId);
      
      // Remove the template from the list
      setTemplates(prev => prev.filter(template => template.id !== templateId));
      
      // Clear selected template if it's the one being deleted
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to delete email template';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedTemplate]);

  const contextValue: EmailTemplateContextType = {
    loading,
    error,
    templates,
    selectedTemplate,
    meta,
    getEmailTemplates,
    getEmailTemplateById,
    createEmailTemplate,
    updateEmailTemplate,
    deleteEmailTemplate,
    setSelectedTemplate,
    clearError,
    clearTemplates,
  };

  return (
    <EmailTemplateContext.Provider value={contextValue}>
      {children}
    </EmailTemplateContext.Provider>
  );
};

// ----------- Hook -----------
export const useEmailTemplate = (): EmailTemplateContextType => {
  const context = useContext(EmailTemplateContext);
  if (context === undefined) {
    throw new Error('useEmailTemplate must be used within an EmailTemplateProvider');
  }
  return context;
};

export default EmailTemplateContext;
